import React, { useCallback, useEffect, useState } from 'react'
import { getMonthlyRewardBoard, type MonthlyRewardResponse, type MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { isMonthlyRewardConfigUpdate, MONTHLY_REWARD_UNAVAILABLE_TEXT } from '../../utils/monthlyRewardTabs'

interface RewardLeaderboardPageProps {
  tab: MonthlyRewardTabKey
}

const RewardLeaderboardPage: React.FC<RewardLeaderboardPageProps> = ({ tab }) => {
  const [data, setData] = useState<MonthlyRewardResponse['data'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await getMonthlyRewardBoard(tab)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
      }
      setData(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void loadBoard()
  }, [loadBoard])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail as {
        type?: string
        promotionType?: string
        contentType?: string
        content?: {
          type?: string
          promotionType?: string
        }
      } | undefined

      const isMonthlyRewardUpdate = isMonthlyRewardConfigUpdate(detail)

      if (isMonthlyRewardUpdate) {
        void loadBoard()
      }
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadBoard])

  useEffect(() => {
    if (!data?.summary?.fakeCount || !data.fakeDataRotatesAt) return
    const rotateAt = new Date(data.fakeDataRotatesAt).getTime()
    if (!Number.isFinite(rotateAt)) return
    const delay = Math.max(1000, rotateAt - Date.now())
    const timer = window.setTimeout(() => {
      void loadBoard()
    }, delay)
    return () => window.clearTimeout(timer)
  }, [data?.fakeDataRotatesAt, data?.summary?.fakeCount, loadBoard])

  const rankBadge = (rank: number): { label: string; className: string } => {
    if (rank === 1) return { label: '🥇', className: 'reward-rank reward-rank--gold' }
    if (rank === 2) return { label: '🥈', className: 'reward-rank reward-rank--silver' }
    if (rank === 3) return { label: '🥉', className: 'reward-rank reward-rank--bronze' }
    return { label: String(rank), className: 'reward-rank' }
  }

  const emptyText = data?.emptyText || 'ยังไม่มีข้อมูลรางวัลประจำเดือน'
  const isPreparingState = emptyText === MONTHLY_REWARD_UNAVAILABLE_TEXT

  return (
    <div className="light_sd border_10 p-3 p-md-4 reward-leaderboard-panel">
      <div className="reward-leaderboard-panel__header">
        <div>
          <h4 className="c_tertiary mb-1">{data?.title || 'รางวัลประจำเดือน'}</h4>
          <p className="mb-0 reward-leaderboard-panel__subtitle">
            {data?.monthLabel || 'เดือนปัจจุบัน'}
            {data?.subtitle ? ` • ${data.subtitle}` : ''}
          </p>
        </div>
        {data?.summary ? (
          <div className="reward-leaderboard-panel__summary">
            อันดับจริง {data.summary.realCount} / เติมข้อมูล {data.summary.fakeCount}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="text-center py-4 c_tertiary">กำลังโหลดข้อมูล...</div>
      ) : error ? (
        <div className="text-center py-4" style={{ color: '#fecaca' }}>{error}</div>
      ) : data?.items?.length ? (
        <>
          <div className="reward-board reward-board--desktop d-none d-md-block">
            <div className="reward-board__head">
              <span className="reward-board__col-rank">อันดับ</span>
              <span className="reward-board__col-user">ยูสเซอร์</span>
              <span className="reward-board__col-metric">{data.metricLabel}</span>
              <span className="reward-board__col-reward">{data.rewardLabel}</span>
            </div>
            <div className="reward-board__list">
              {data.items.map((row) => {
                const badge = rankBadge(row.rank)
                return (
                  <div
                    key={`${tab}-desktop-${row.rank}`}
                    className={`reward-board__row${row.rank <= 3 ? ' reward-board__row--top' : ''}`}
                  >
                    <span className="reward-board__col-rank">
                      <span className={badge.className}>{badge.label}</span>
                    </span>
                    <span className="reward-board__col-user">{row.user}</span>
                    <span className="reward-board__col-metric">{row.metricValue}</span>
                    <span className="reward-board__col-reward">{row.reward}</span>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="reward-board-mobile d-md-none">
            {data.items.map((row) => {
              const badge = rankBadge(row.rank)
              return (
                <div
                  key={`${tab}-mobile-${row.rank}`}
                  className={`reward-board-mobile__card${row.rank <= 3 ? ' reward-board-mobile__card--top' : ''}`}
                >
                  <div className="reward-board-mobile__top">
                    <span className={badge.className}>{badge.label}</span>
                    <span className="reward-board-mobile__user">{row.user}</span>
                  </div>
                  <div className="reward-board-mobile__details">
                    <div className="reward-board-mobile__detail">
                      <span className="reward-board-mobile__label">{data.metricLabel}</span>
                      <span className="reward-board-mobile__value">{row.metricValue}</span>
                    </div>
                    <div className="reward-board-mobile__detail reward-board-mobile__detail--reward">
                      <span className="reward-board-mobile__label">{data.rewardLabel}</span>
                      <span className="reward-board-mobile__value">{row.reward}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className={`text-center py-4 reward-leaderboard-panel__empty${isPreparingState ? ' reward-leaderboard-panel__empty--preparing' : ''}`}>
          {isPreparingState ? <div className="reward-leaderboard-panel__empty-icon" aria-hidden="true">✨</div> : null}
          <p className="mb-0 c_tertiary">{emptyText}</p>
        </div>
      )}
    </div>
  )
}

export default RewardLeaderboardPage
